import React from 'react'
import { Col, Form as FormB } from 'react-bootstrap'
import FormSelect from '../FormSelect'
import TooltipOnHover from '../TooltipOnHover'
import { isMobile } from '../../helpers'
import {
  CHAUFFEUR_ARRIVAL_OPTIONS,
  chauffeurArrivalDriverTitle,
  estimatedArrivalText,
  isChauffeurArrivalBuffer,
} from '../../helpers/chauffeurArrivalBuffer'

const { Row: FormRow } = FormB

// RD-4074, Figma 14186:105032 (select) / 14186:105151 (VIP) / 14187:107378 (details, VIP).
export const CHAUFFEUR_ARRIVAL_VIP_TEXT =
  'With VIP Meet & Greet, the chauffeur arrival time is automatically aligned with the flight arrival.'

// RD-4144: `label` / `valueText` reuse the banner for the admin's manual pick-up ("Chauffeur
// arrival time · 15 minutes after flight arrival").
export function EstimatedArrivalBanner({
  estimated,
  fullWidth = false,
  wide = false,
  tooltip,
  label = 'Estimated chauffeur arrival',
  valueText = null,
}) {
  if (!estimated && !valueText) return null
  return (
    <div
      className={`chauffeur-arrival-banner${
        fullWidth ? ' chauffeur-arrival-banner--full' : ''
      }${wide ? ' chauffeur-arrival-banner--wide' : ''}`}
    >
      <span className='chauffeur-arrival-banner__icon'>
        <img alt='' src='/images/icons/chauffeur-arrival-clock.svg' />
      </span>
      <div className='chauffeur-arrival-banner__text'>
        <span className='chauffeur-arrival-banner__label'>{label}</span>
        <span className='chauffeur-arrival-banner__value'>
          {valueText || estimatedArrivalText(estimated)}
          {tooltip ? (
            <TooltipOnHover
              delay={200}
              placement='right-start'
              isMobileBrowser={isMobile()}
              onMouseEnter={() => {}}
              component={<div>{tooltip}</div>}
            >
              <span className='chauffeur-arrival-banner__info'>
                <img alt='' src='/images/icons/icon-information.png' />
              </span>
            </TooltipOnHover>
          ) : null}
        </span>
      </div>
    </div>
  )
}

// Partner booking details (RD-4074), Figma driver banner:
//  - verified: buffer title + flight arrival (tracked landing, estimated when late) + track
//    instruction
//  - unverified: buffer/landing title + track instruction, no flight time
//  - invalid / none: do not render
// RD-4144 `manual`: an admin set the pick-up by hand, so it is not the landing + buffer and the
// flight arrival line is left out.
export function DriverChauffeurArrivalBanner({
  variant,
  buffer,
  scheduledFlightArrival,
  flightArrivalEstimated = false,
  manual = false,
}) {
  if (variant === 'verified') {
    if (!isChauffeurArrivalBuffer(buffer)) return null
    const title = chauffeurArrivalDriverTitle(buffer)
    const scheduledText = estimatedArrivalText(scheduledFlightArrival)
    if (!title || (!manual && !scheduledText)) return null
    return (
      <div className='driver-chauffeur-arrival-banner'>
        <div className='driver-chauffeur-arrival-banner__content'>
          <div className='driver-chauffeur-arrival-banner__title-row'>
            <span className='driver-chauffeur-arrival-banner__icon'>
              <img alt='' src='/images/icons/chauffeur-arrival-plane.svg' />
            </span>
            <span className='driver-chauffeur-arrival-banner__title'>
              {title}
            </span>
          </div>
          <p className='driver-chauffeur-arrival-banner__body'>
            {manual ? null : (
              <>
                {flightArrivalEstimated ? 'Estimated' : 'Scheduled'} flight
                arrival: {scheduledText}
                <br />
              </>
            )}
            You must track the flight and adjust your arrival time accordingly.
          </p>
        </div>
      </div>
    )
  }

  if (variant === 'unverified') {
    return (
      <div className='driver-chauffeur-arrival-banner'>
        <div className='driver-chauffeur-arrival-banner__content'>
          <div className='driver-chauffeur-arrival-banner__title-row'>
            <span className='driver-chauffeur-arrival-banner__icon'>
              <img alt='' src='/images/icons/chauffeur-arrival-plane.svg' />
            </span>
            <span className='driver-chauffeur-arrival-banner__title'>
              Arrive when the flight lands
            </span>
          </div>
          <p className='driver-chauffeur-arrival-banner__body'>
            You must track the flight and adjust your arrival time accordingly.
          </p>
        </div>
      </div>
    )
  }

  return null
}

// The app's FormSelect, driven as a controlled select: the buffer is stored as a plain number
// (an option object with value 0 would be dropped by the payload builders' truthy copy).
export function ChauffeurArrivalSelect({ name, label, value, onChange }) {
  return (
    <FormSelect
      field={{
        name,
        value: CHAUFFEUR_ARRIVAL_OPTIONS.find(o => o.value === value) || null,
        onBlur: () => {},
      }}
      form={{
        touched: {},
        errors: {},
        setFieldValue: (_, option) => option && onChange(option.value),
        setTouched: () => {},
      }}
      label={label}
      options={CHAUFFEUR_ARRIVAL_OPTIONS}
    />
  )
}

export default function ChauffeurArrivalSection({
  mode,
  value,
  onChange,
  estimated,
  isB2C = false,
}) {
  const rowStyle = isB2C ? { width: '100%' } : {}
  return (
    <div className='chauffeur-arrival-section'>
      <h4 className='sub-header chauffeur-arrival-section__title'>
        Chauffeur arrival time
      </h4>
      {mode === 'vip' ? (
        <>
          <p className='chauffeur-arrival-section__intro'>
            {CHAUFFEUR_ARRIVAL_VIP_TEXT}
          </p>
          <EstimatedArrivalBanner estimated={estimated} fullWidth />
        </>
      ) : (
        <>
          <p className='chauffeur-arrival-section__intro'>
            Select when you would like the chauffeur to arrive after the flight
            lands. <br className='chauffeur-arrival-section__break' />
            We’ll monitor the flight and adjust the arrival time automatically.
          </p>
          <FormRow style={rowStyle} className='chauffeur-arrival-section__row'>
            <Col md='6'>
              <ChauffeurArrivalSelect
                name='chauffeurArrivalBuffer'
                label='CHAUFFEUR ARRIVAL TIME'
                value={value}
                onChange={onChange}
              />
            </Col>
            <Col md='6' className='chauffeur-arrival-section__banner-col'>
              <EstimatedArrivalBanner estimated={estimated} />
            </Col>
          </FormRow>
        </>
      )}
    </div>
  )
}
